import { isInEnum } from '@caresend/utils';
import { isMobile } from 'mobile-device-detect';
import { computed } from 'vue';
import { decryptFile } from '../../../database/encryption/decrypt.js';
import 'js-sha256';
import '../../../database/firebaseConfig/cloudFunctions.js';
import '@caresend/types';
import '@sentry/vue';
import { toastErrorAndReport } from '../../../functions/toast/toast.js';
import { DownloadOption } from '../../../database/encryption/model.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import '../../../functions/tracking/index.js';

const getDownloadOptions = (desktopOptions, mobileOptions, openLabel, downloadLabel) => computed(() => {
    const options = isMobile
        ? mobileOptions
        : desktopOptions;
    return options.map((option) => {
        if (!isInEnum(option, DownloadOption)) {
            throw Error('Unexpected download option');
        }
        const label = option === DownloadOption.OPEN
            ? openLabel
            : downloadLabel;
        const icon = option === DownloadOption.OPEN
            ? 'external-link-alt'
            : 'arrow-alt-circle-down';
        return {
            label,
            value: option,
            icon,
        };
    });
});
/**
 * Returns string in open mode, and blob in download mode.
 */
const decryptHelper = async (encryptedFile, downloadOption) => {
    if (downloadOption === DownloadOption.OPEN) {
        return decryptFile(encryptedFile, true);
    }
    return decryptFile(encryptedFile, false);
};
/**
 * Only use if certain that the value is a blob. Helps to narrow
 * to the correct type.
 */
function assertIsBlob(urlOrBlob) {
    if (!(urlOrBlob instanceof Blob)) {
        throw Error(`Expected blob, but got ${typeof urlOrBlob} when downloading file.`);
    }
}
/**
 * Only use if certain that the value is a blob. Helps to narrow
 * to the correct type.
 */
function assertIsString(urlOrBlob) {
    if (typeof urlOrBlob !== 'string') {
        throw Error(`Expected string, but got ${typeof urlOrBlob} when downloading file.`);
    }
}
/**
 * Get the file extension from a URL.
 *
 * Example:
 * `https://m.b.g/44/333/f-f-f/oho.pdf?query=1#hash` => `pdf`
 */
const getFileExtension = (url) => { var _a, _b, _c; return (_c = (_b = (_a = url.split(/[#?]/)[0]) === null || _a === void 0 ? void 0 : _a.split('.').pop()) === null || _b === void 0 ? void 0 : _b.trim()) !== null && _c !== void 0 ? _c : url; };
const downloadOrOpenFile = async (file, downloadOption) => {
    var _a;
    // Mobile Safari async window open workaround (3/3)
    let newWindow = null;
    if (isMobile && downloadOption === DownloadOption.OPEN) {
        newWindow = window.open('about:blank', '_blank');
    }
    let urlOrBlob = (_a = file.url) !== null && _a !== void 0 ? _a : null;
    let fileName;
    try {
        if (file.encryptedFile) {
            urlOrBlob = await decryptHelper(file.encryptedFile, downloadOption);
            fileName = file.encryptedFile.metadata.fileName;
        }
        if (!urlOrBlob) {
            throw Error('No file to open or download.');
        }
        if (downloadOption === DownloadOption.OPEN) {
            assertIsString(urlOrBlob);
            const url = urlOrBlob;
            // Mobile Safari async window open workaround (1/3)
            // https://stackoverflow.com/a/39387533
            if (newWindow) {
                // ESLint rule disabled as no other known workaround for iOS
                // eslint-disable-next-line no-param-reassign
                newWindow.location = url;
            }
            else {
                window.open(url, '_blank');
            }
        }
        else {
            if (!file.encryptedFile) {
                assertIsString(urlOrBlob);
                const url = urlOrBlob;
                fileName = `download.${getFileExtension(url)}`;
                if (fetch) {
                    // Cloud storage signed URLs are not compatible on their own with
                    // FileSaver. We will fetch the blob with built-in fetch API first.
                    // If the browser does not support fetch API, the file will open
                    // in a new tab as if the user clicked Open.
                    const response = await fetch(url);
                    urlOrBlob = await response.blob();
                }
                else {
                    saveAs(url, fileName);
                    return;
                }
            }
            assertIsBlob(urlOrBlob);
            const blob = urlOrBlob;
            // This is a workaround for an issue in which FileSaver opens the file
            // in the current tab in Mobile safari. With this change, the user
            // is prompted to download the file. It is important for a filename
            // with extension to be present when using this method, so the device
            // knows how to open the file.
            // https://github.com/eligrey/FileSaver.js/issues/581#issuecomment-952736111
            // https://github.com/eligrey/FileSaver.js/issues/375#issuecomment-408213567
            const blobWithModifiedMimeType = new Blob([blob], { type: 'application/octet-stream' });
            // Testing note: `saveAs` functionality varies by browser and behaves
            // differently in an iframe. If testing in storybook, open the story
            // directly rather than within the storybook iframe.
            saveAs(blobWithModifiedMimeType, fileName);
        }
    }
    catch (error) {
        toastErrorAndReport(error);
        // Mobile Safari async window open workaround (2/3)
        if (newWindow)
            newWindow.close();
    }
};

export { decryptHelper, downloadOrOpenFile, getDownloadOptions };
